if (!customElements.get('momento-product-showcase')) {
  class MomentoProductShowcase extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.mainImage = this.querySelector('[data-product-main-image]');
      this.productLink = this.querySelector('[data-product-link]');
      this.price = this.querySelector('[data-product-price]');
      this.comparePrice = this.querySelector('[data-product-compare-price]');
      this.variantInput = this.querySelector('[data-product-variant-id]');
      this.productForm = this.querySelector('.momento-product-form');
      this.colorLabel = this.querySelector('[data-product-color-label]');
      this.swatches = [...this.querySelectorAll('[data-product-color]')];
      this.thumbnails = [...this.querySelectorAll('[data-product-thumbnail]')];
      this.previousButton = this.querySelector('[data-gallery-previous]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.reviewLink = this.querySelector('[data-product-reviews]');
      this.soldOutBadge = this.querySelector('[data-product-sold-out-badge]');

      try {
        this.products = JSON.parse(this.querySelector('[data-product-showcase-data]').textContent);
      } catch (_error) {
        return;
      }

      this.selectedColor = 'silver-black';
      this.activeIndex = 0;
      this.bindEvents();
      this.updateProduct();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    bindEvents() {
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          this.selectedColor = swatch.dataset.productColor;
          this.activeIndex = 0;
          this.updateProduct();
        }, { signal: this.signal });
      });

      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          this.activeIndex = Number(thumbnail.dataset.productThumbnail);
          this.updateGallery();
        }, { signal: this.signal });

        thumbnail.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;

          event.preventDefault();
          const index = Number(thumbnail.dataset.productThumbnail);
          if (event.key === 'Home') this.activeIndex = 0;
          else if (event.key === 'End') this.activeIndex = this.currentProduct.gallery.length - 1;
          else {
            const direction = event.key === 'ArrowRight' ? 1 : -1;
            this.activeIndex = (index + direction + this.currentProduct.gallery.length) % this.currentProduct.gallery.length;
          }

          this.updateGallery();
          this.thumbnails[this.activeIndex]?.focus();
        }, { signal: this.signal });
      });

      this.previousButton?.addEventListener('click', () => this.cycleImage(-1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.cycleImage(1), { signal: this.signal });
      this.reviewLink?.addEventListener('click', (event) => event.preventDefault(), { signal: this.signal });
    }

    updateProduct() {
      this.currentProduct = this.products[this.selectedColor];
      if (!this.currentProduct) return;

      this.swatches.forEach((swatch) => {
        const selected = swatch.dataset.productColor === this.selectedColor;
        swatch.classList.toggle('active', selected);
        swatch.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      if (this.colorLabel) this.colorLabel.textContent = this.currentProduct.label;
      if (this.productLink) this.productLink.href = this.currentProduct.url;
      if (this.price) this.price.textContent = this.currentProduct.price;

      if (this.comparePrice) {
        this.comparePrice.textContent = this.currentProduct.comparePrice || '';
        this.comparePrice.hidden = !this.currentProduct.comparePrice;
      }

      if (this.variantInput) {
        this.variantInput.value = this.currentProduct.variantId;
        this.variantInput.disabled = !this.currentProduct.available;
      }

      if (this.productForm) {
        this.productForm.dataset.productId = this.currentProduct.productId;
        this.productForm.dataset.available = String(this.currentProduct.available);
      }

      if (this.soldOutBadge) this.soldOutBadge.hidden = !this.currentProduct.soldOut;

      this.dispatchEvent(new CustomEvent('momento:product-color-change', {
        bubbles: true,
        detail: { color: this.selectedColor, available: this.currentProduct.available }
      }));

      this.updateGallery();
    }

    updateGallery() {
      const gallery = this.currentProduct?.gallery;
      if (!gallery?.length) return;

      const image = gallery[this.activeIndex] || gallery[0];
      this.activeIndex = gallery.indexOf(image);
      this.mainImage.src = image.src;
      this.mainImage.alt = image.alt;
      this.restartImageAnimation();

      this.thumbnails.forEach((thumbnail, index) => {
        const thumbnailImage = thumbnail.querySelector('img');
        const galleryImage = gallery[index];
        const selected = index === this.activeIndex;

        thumbnailImage.src = galleryImage.src;
        thumbnail.classList.toggle('active', selected);
        thumbnail.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
        thumbnail.tabIndex = selected ? 0 : -1;
      });
    }

    cycleImage(direction) {
      const count = this.currentProduct.gallery.length;
      this.activeIndex = (this.activeIndex + direction + count) % count;
      this.updateGallery();
    }

    restartImageAnimation() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.mainImage.style.animation = 'none';
      this.mainImage.offsetWidth;
      this.mainImage.style.removeProperty('animation');
    }
  }

  customElements.define('momento-product-showcase', MomentoProductShowcase);
}
